import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProductAccess, useSetProductAccess } from "../hooks/useProductAccess";

export function ProductAccessTab({ userId }: { userId: string }) {
  const { data: current = [], isLoading } = useProductAccess(userId);
  const setAccess = useSetProductAccess(userId);
  const { data: productsResult } = useProductList({ page: 1, limit: 200 });
  const products = productsResult?.data ?? [];

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSelected(new Set(current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
    );
  }, [products, search]);

  const isRestricted = selected.size > 0;
  const dirty = useMemo(() => {
    if (selected.size !== current.length) return true;
    return current.some((id) => !selected.has(id));
  }, [selected, current]);

  function toggle(id: string) {
    setSaved(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    await setAccess.mutateAsync(Array.from(selected));
    setSaved(true);
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-foreground">Product Access (QA)</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Assign specific products to restrict this user's QA Issues access. When
          one or more products are selected, the user can only see and create
          issues for those products.{" "}
          <span className="font-medium text-foreground">
            Leave everything unchecked to keep the user unrestricted
          </span>{" "}
          (sees all products — the default). This scoping applies to QA Issues only.
        </p>
      </div>

      <div
        className="rounded-md border px-3 py-2 text-xs"
        style={
          isRestricted
            ? { borderColor: "hsl(38,92%,70%)", background: "hsl(38,92%,96%)", color: "hsl(38,92%,30%)" }
            : { borderColor: "hsl(142,55%,75%)", background: "hsl(142,55%,96%)", color: "hsl(142,72%,28%)" }
        }
      >
        {isRestricted
          ? `Restricted to ${selected.size} product${selected.size === 1 ? "" : "s"}.`
          : "Unrestricted — this user sees all products."}
      </div>

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search products..."
        className="max-w-xs"
      />

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading...
        </div>
      ) : (
        <div className="max-h-80 space-y-1 overflow-y-auto rounded-md border p-2" style={{ borderColor: "hsl(210,30%,88%)" }}>
          {filtered.length === 0 ? (
            <p className="py-3 text-center text-sm text-muted-foreground">No products found.</p>
          ) : (
            filtered.map((p) => (
              <label
                key={p.id}
                className="flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 hover:bg-[hsl(210,50%,98%)]"
              >
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  onChange={() => toggle(p.id)}
                  className="h-4 w-4"
                />
                <span className="text-sm text-foreground">{p.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
              </label>
            ))
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={!dirty || setAccess.isPending}>
          {setAccess.isPending && (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
          )}
          Save Product Access
        </Button>
        {saved && !dirty && (
          <span className="text-xs text-green-700">Saved.</span>
        )}
      </div>
    </div>
  );
}
